import type { ReactNode } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faUserTie } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import { Button } from "@/components/ui/button/Button";
import { SegmentedPillToggle } from "@/components/ui/segmented-tabs/SegmentedPillToggle";
import { AgenticCaseProgressIndicator } from "@/components/case-checking/AgenticCaseProgressIndicator";
import { CC_HEADER_META_CHIP_ICON } from "@/components/case-checking/caseCheckingChrome";
import {
  HumanReviewStatusChip,
  SummaryReviewGradePill,
  type HumanReviewRouting,
} from "@/components/case-checking/caseCheckingStatusBadges";

type ReviewHeaderGrade = "suitable" | "dev-needs" | "unclear" | "unsuitable" | "stop";

export type CaseShellMode = "summary" | "review" | "detail";

const CASE_SHELL_MODE_OPTIONS: { id: CaseShellMode; label: string }[] = [
  { id: "summary", label: "Case Summary" },
  { id: "review", label: "Case Review" },
  { id: "detail", label: "Case Detail" },
];

export function CaseShellModeToggle({
  mode,
  onChange,
}: {
  mode: CaseShellMode;
  onChange: (next: CaseShellMode) => void;
}) {
  return (
    <SegmentedPillToggle
      options={CASE_SHELL_MODE_OPTIONS}
      value={mode}
      onChange={onChange}
      aria-label="Case view mode"
      data-testid="case-shell-mode-toggle"
      optionTestIdPrefix="case-shell-mode"
    />
  );
}

export function CaseReviewHeader({
  clientTitle,
  scopeNote,
  grade,
  humanReview,
  reviewerLine,
  shellMode,
  onShellModeChange,
  trailingActions,
  hideClientTitle = false,
  showShellModeToggle = true,
  showGrade = true,
  progressLabel = null,
  progressTitle,
  progressVariant = "running",
  onProgressClick,
}: {
  clientTitle: string;
  scopeNote?: string | null;
  grade: ReviewHeaderGrade | null;
  humanReview?: HumanReviewRouting | null;
  reviewerLine?: string | null;
  shellMode: CaseShellMode;
  onShellModeChange: (mode: CaseShellMode) => void;
  trailingActions?: ReactNode;
  /** When the OS window chrome already shows the case name. */
  hideClientTitle?: boolean;
  /** When false, reviewers stay in guided Case Review (no Summary/Detail toggle). */
  showShellModeToggle?: boolean;
  /** Hide grade pill while automated file check is still running. */
  showGrade?: boolean;
  /** Marvin mission progress label (aligned with shell mode toggle). */
  progressLabel?: string | null;
  progressTitle?: string | null;
  progressVariant?: "running" | "blocked" | "idle";
  /** Opens the api-runner task progress dialog when the progress badge is clicked. */
  onProgressClick?: () => void;
}) {
  const scopeTooltip = scopeNote ? (
    <Tooltip
      content={<p className="text-sm leading-relaxed whitespace-pre-wrap max-w-sm">{scopeNote}</p>}
      placement="bottom"
      maxWidth="24rem"
      clickOnly
    >
      <Button
        plain
        size="unstyled"
        type="button"
        className={clsx(
          CC_HEADER_META_CHIP_ICON,
          "!h-6 !min-h-6 !w-6 !rounded-md",
          "hover:!bg-slate-200/80 dark:hover:!bg-white/[0.08]",
          "shrink-0 text-slate-500 transition-colors hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200",
        )}
        aria-label="View advice scope note"
        data-testid="case-scope-tooltip"
      >
        <FontAwesomeIcon icon={faCircleInfo} className="h-3.5 w-3.5" aria-hidden />
      </Button>
    </Tooltip>
  ) : null;

  return (
    <div data-testid="case-review-header">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
          {progressLabel ? (
            <AgenticCaseProgressIndicator
              label={progressLabel}
              size={progressVariant === "blocked" ? "minimized" : "lg"}
              variant={progressVariant}
              title={progressTitle ?? undefined}
              testId="case-review-header-progress"
              className={progressVariant === "blocked" ? "max-w-[5.75rem] shrink-0" : "shrink-0"}
              onClick={onProgressClick}
            />
          ) : null}
          {!hideClientTitle ? (
            <h1 className="truncate text-xl font-semibold text-slate-900 dark:text-slate-100">{clientTitle}</h1>
          ) : (
            <h1 className="sr-only">{clientTitle}</h1>
          )}
          <div className="flex shrink-0 items-center gap-2">
            {scopeTooltip}
            {showGrade && grade ? <SummaryReviewGradePill grade={grade} size="compact" /> : null}
            <HumanReviewStatusChip value={humanReview} />
          </div>
          {reviewerLine ? (
            <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
              <FontAwesomeIcon
                icon={faUserTie}
                className="h-3.5 w-3.5 shrink-0 text-slate-500 dark:text-slate-400"
                aria-hidden
              />
              <span className="truncate">{reviewerLine}</span>
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showShellModeToggle ? (
            <CaseShellModeToggle mode={shellMode} onChange={onShellModeChange} />
          ) : null}
          {trailingActions}
        </div>
      </div>
    </div>
  );
}
